/**
 * Which assessed routes end at this agent.
 *
 * Exposure's depth on an object Core already shows, attached the way
 * `permission_fit` attaches — joined on `agent_id_hint`, rendered inside the
 * card. There was briefly a tab named after the SKU; a tab couples the
 * information architecture to the price list, and the Core → Exposure reading
 * ("this agent reaches the payments database" → "and here is the live path to
 * it") only works when both sentences are in the same place.
 *
 * Renders NOTHING when there are no routes, which is deliberate.
 * `agent_id_hint` embeds the definition's line number, so it is a within-scan
 * key: an agent whose hint predates the current scan is absent from the index
 * for a reason that has nothing to do with its exposure. "No routes reach this
 * agent" would be a claim this cannot stand behind, and `AgentReaches` already
 * established the rule — it returns null rather than rendering "reaches: none",
 * because that row would be a claim.
 *
 * The estate-level counts are NOT here. A per-agent card showing four verdict
 * totals would repeat the whole estate on every card; the unfiltered counts
 * belong with the component inventory, where the estate is the subject.
 */
import { Box, Chip, Tooltip, Typography } from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import { AttackPath, InjectionVerdict } from '../services/api'
import { VERDICT, VERDICT_ORDER, worstVerdict } from './exposureVerdicts'
import BundleLocked from './BundleLocked'

interface AgentRoutesProps {
  routes: AttackPath[]
  /** The route refused for want of ARIAS Exposure. Kept apart from an empty
   *  `routes`, which stays silent because `agent_id_hint` is a within-scan key
   *  and absence has a mechanical cause this cannot distinguish. */
  notEntitled?: boolean
}

const AgentRoutes = ({ routes, notEntitled }: AgentRoutesProps) => {
  if (notEntitled) {
    return (
      <BundleLocked feature="attack_paths" label="Reachable routes" />
    )
  }
  if (!routes.length) return null

  const worst = worstVerdict(routes.map((r) => r.verdict) as InjectionVerdict[])
  if (!worst) return null

  const byVerdict = VERDICT_ORDER.map((v) => ({
    verdict: v,
    count: routes.filter((r) => r.verdict === v).length,
  })).filter((x) => x.count > 0)

  // Tools named on the routes that are actually live. A delete-tier tool on a
  // `controlled` route is a different sentence from one on a `reachable` route,
  // and listing both together would flatten the distinction the verdict makes.
  const liveTools = [
    ...new Set(
      routes
        .filter((r) => r.verdict === 'reachable')
        .flatMap((r) => r.consequential_tools),
    ),
  ]

  return (
    <Box sx={{ mt: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">
          routes in:
        </Typography>
        {byVerdict.map(({ verdict, count }) => (
          <Tooltip key={verdict} title={VERDICT[verdict].meaning}>
            <Chip
              size="small"
              variant="outlined"
              color={VERDICT[verdict].color}
              label={`${count} ${VERDICT[verdict].label}`}
              icon={
                verdict === 'not_assessable'
                  ? <HelpOutline sx={{ fontSize: 13 }} />
                  : undefined
              }
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          </Tooltip>
        ))}
      </Box>
      {liveTools.length > 0 && (
        <Tooltip title="Tools this agent holds that change something, on a route where no control was found. Read is not consequential for this question — an injection that causes a read has not crossed the boundary a write, an execution or a deletion crosses.">
          <Typography variant="caption" color="error.main" display="block">
            reachable with: {liveTools.join(', ')}
          </Typography>
        </Tooltip>
      )}
    </Box>
  )
}

export default AgentRoutes
